import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, LANGS } from "@/lib/content";
import { IMG } from "@/lib/images";
import { useLang } from "@/i18n/LanguageContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { BookConsultation } from "@/components/BookConsultation";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { lang, setLang, c } = useLang();
  const { config } = useSiteConfig();
  const hasCalendly = !!(config.calendly_url || "").trim();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const dark = scrolled;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        dark ? "bg-white/85 backdrop-blur-xl border-b border-olime-line py-3" : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="container-olime flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="shrink-0">
          <img
            src={dark ? IMG.logoTeal : IMG.logoWhite}
            alt="O'Lime Design"
            className="h-16 md:h-20 lg:h-24 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `link-underline text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  dark ? "text-olime-ink" : "text-white"
                } ${isActive ? "opacity-100" : "opacity-70 hover:opacity-100"}`
              }
            >
              {c.nav[item.key]}
            </NavLink>
          ))}
          {hasCalendly && (
            <BookConsultation
              variant="nav"
              testId="nav-book"
              showIcon={false}
              label={c.cta.book}
              className={`link-underline text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 opacity-70 hover:opacity-100 ${
                dark ? "text-olime-ink" : "text-white"
              }`}
            />
          )}
        </nav>

        <div className="flex items-center gap-5">
          <div className="hidden md:flex items-center gap-2">
            {LANGS.map((l) => (
              <button
                key={l}
                data-testid={`lang-${l.toLowerCase()}`}
                onClick={() => setLang(l.toLowerCase())}
                className={`text-[11px] tracking-[0.15em] transition-colors duration-300 ${dark ? "text-olime-ink" : "text-white"} ${
                  lang === l.toLowerCase() ? "opacity-100 underline underline-offset-4" : "opacity-50 hover:opacity-90"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          {hasCalendly ? (
            <BookConsultation
              variant={dark ? "header" : "headerLight"}
              testId="header-book-cta"
              label={c.cta.book}
              className="hidden sm:inline-flex"
            />
          ) : (
            <Link
              to="/contact#form"
              data-testid="header-book-cta"
              className={`hidden sm:inline-flex items-center px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] transition-colors duration-400 ${
                dark ? "bg-olime-ink text-olime-bg hover:bg-olime-stone hover:text-olime-ink" : "border border-white/70 text-white hover:bg-white hover:text-olime-ink"
              }`}
            >
              {c.cta.book}
            </Link>
          )}

          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} className={`lg:hidden ${dark ? "text-olime-ink" : "text-white"}`} aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-olime-line mt-3"
          >
            <div className="container-olime py-8 flex flex-col gap-5">
              {NAV.map((item) => (
                <NavLink key={item.to} to={item.to} data-testid={`mobile-nav-${item.key}`} className="font-serif text-3xl text-olime-ink">
                  {c.nav[item.key]}
                </NavLink>
              ))}
              <div className="flex gap-4 pt-2">
                {LANGS.map((l) => (
                  <button key={l} onClick={() => setLang(l.toLowerCase())} className={`text-sm ${lang === l.toLowerCase() ? "text-olime-ink underline" : "text-olime-muted"}`}>
                    {l}
                  </button>
                ))}
              </div>
              {hasCalendly ? (
                <BookConsultation variant="ink" testId="mobile-book-cta" label={c.cta.book} className="mt-2 w-full" />
              ) : (
                <Link to="/contact#form" data-testid="mobile-book-cta" className="btn-ink mt-2 w-full">{c.cta.book}</Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
